/**
 * De generatienaad aan de kant van de pagina (ontwerp 4.6 en 9.2, eisenboek 12.50).
 *
 * Na een compactie heeft een sectie een nieuwe generatie: dezelfde inhoud onder andere Yjs-items. De pod
 * sluit dan het document van die sectie met `nieuwe-generatie` (4003, `CLOSE_REASONS` in
 * `apps/runtime/src/collaboration.ts`), en stuurt vlak daarvoor een bericht met de eindsnapshot: wat hij
 * van de vorige generatie had bij de naad. Dezelfde sluiting komt als deze pagina terugkomt met een
 * document van vóór de naad, bijvoorbeeld na een slaapstand. De pagina gooit dan haar document en haar
 * provider voor die sectie weg, haalt het document opnieuw op, en zet daarin de eigen invoer terug die de
 * pod niet had (`carryOverAcrossSeam` in `@eformulieren/renderer/document`).
 *
 * Eén document en niet de socket: dan komt de sluiting als sluitbericht, met code 1000 en de reden
 * (`MessageReceiver.ts` in `@hocuspocus/provider`, zoals bij `toegang-gewijzigd` in `access.ts`).
 */

const NEW_GENERATION = { code: 4003, reden: 'nieuwe-generatie' } as const

type Closing = { readonly code?: number; readonly reason?: string } | undefined

export function isNewGenerationClose(closing: Closing): boolean {
  if (closing === undefined) return false
  return closing.code === NEW_GENERATION.code || closing.reason === NEW_GENERATION.reden
}

/** Het bericht van de pod bij de naad: welke generatie deze pagina had, en de eindsnapshot daarvan. */
export interface NewGeneration {
  readonly sectie: string
  readonly generatie: number
  readonly eindsnapshot: Uint8Array
}

/** Leest `{"soort":"nieuwe-generatie","sectie":…,"generatie":…,"eindsnapshot":"<base64>"}`; al het andere is het niet. */
export function newGenerationOf(payload: string): NewGeneration | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(payload)
  } catch {
    return undefined
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined
  const message = parsed as { soort?: unknown; sectie?: unknown; generatie?: unknown; eindsnapshot?: unknown }
  if (message.soort !== 'nieuwe-generatie' || typeof message.sectie !== 'string' || typeof message.generatie !== 'number' || typeof message.eindsnapshot !== 'string') {
    return undefined
  }
  let bytes: Uint8Array
  try {
    bytes = Uint8Array.from(atob(message.eindsnapshot), (character) => character.charCodeAt(0))
  } catch {
    return undefined
  }
  return { sectie: message.sectie, generatie: message.generatie, eindsnapshot: bytes }
}
